import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/AppLayout';
import { S, Icon, PageHeader, BtnPrimary, BtnSecondary, TH, TD, EmptyState } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// ─── Fallback data (commented out — no longer used for initialization) ───
// const FALLBACK_STATS = {
//   totalInteractions: 147, totalInputTokens: 284500, totalOutputTokens: 96200,
//   uniqueUsers: 8, uniqueClaims: 34, estimatedCostUSD: '2.2985',
// };
// const FALLBACK_LOGS = [ /* ... removed for brevity ... */ ];

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 14, padding: '20px 24px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: color || S.textPrimary, letterSpacing: -0.5 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: S.textMuted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function fmtTokens(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function fmtTime(ts) {
  return new Date(ts).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function hashShort(h) {
  if (!h) return '—';
  return h.length > 12 ? `${h.slice(0, 6)}…${h.slice(-4)}` : h;
}

// ─── Detail modal ─────────────────────────────────────────
function DetailModal({ log, onClose }) {
  const { t } = useTranslation();
  if (!log) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 600, width: '100%', maxHeight: '80vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary }}>{t('screens.aiGovernance.detailTitle')}</div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, color: S.textMuted }}>✕</button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {[
              [t('screens.aiGovernance.detailTimestamp'),     fmtTime(log.timestamp)],
              [t('screens.aiGovernance.detailUser'),          log.user?.name || '—'],
              [t('screens.aiGovernance.detailClaim'),         log.content?.claimNumber || '—'],
              [t('screens.aiGovernance.detailModel'),         log.content?.modelUsed || '—'],
              [t('screens.aiGovernance.detailInputTokens'),  (log.content?.inputTokens || 0).toLocaleString()],
              [t('screens.aiGovernance.detailOutputTokens'), (log.content?.outputTokens || 0).toLocaleString()],
              [t('screens.aiGovernance.detailContextHash'),  log.content?.contextHash || '—'],
              [t('screens.aiGovernance.detailResponseHash'), log.content?.responseHash || '—'],
              [t('screens.aiGovernance.detailQuestion'),      log.content?.questionSummary || '—'],
            ].map(([k, v]) => (
              <tr key={k} style={{ borderBottom: `1px solid ${S.borderLight}` }}>
                <td style={{ padding: '10px 0', fontWeight: 600, color: S.textMuted, width: 140, verticalAlign: 'top' }}>{k}</td>
                <td style={{ padding: '10px 0', color: S.textPrimary, wordBreak: 'break-all' }}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// AI GOVERNANCE SCREEN
// ═══════════════════════════════════════════════════════════
const AIGovernanceScreen = () => {
  const { t } = useTranslation();
  const [stats, setStats]       = useState({});
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch]     = useState('');
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE_URL}/reggie/governance`, {
        params: { page, limit: 50, claimNumber: search || undefined },
        withCredentials: true,
      });
      if (data?.data) {
        setLogs(data.data.logs || []);
        setStats(data.data.stats || {});
        setTotalPages(data.data.pages || 1);
      }
    } catch {
      // show empty state on error
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCSV = () => {
    const header = 'Timestamp,User,Claim,Question,Model,Input Tokens,Output Tokens,Context Hash,Response Hash\n';
    const rows = logs.map(l =>
      [
        fmtTime(l.timestamp),
        l.user?.name || '',
        l.content?.claimNumber || '',
        `"${(l.content?.questionSummary || '').replace(/"/g, '""')}"`,
        l.content?.modelUsed || '',
        l.content?.inputTokens || 0,
        l.content?.outputTokens || 0,
        l.content?.contextHash || '',
        l.content?.responseHash || '',
      ].join(',')
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'reggie-governance-log.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <AppLayout>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{display:'flex',justifyContent:'center',alignItems:'center',padding:60}}><div style={{width:36,height:36,border:'3px solid #e2e8f0',borderTopColor:'#7c3aed',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/></div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div style={{ flex: 1, overflow: 'auto', padding: '32px 40px' }}>
        <PageHeader
          title={t('screens.aiGovernance.title')}
          subtitle={t('screens.aiGovernance.subtitle')}
          actions={
            <div style={{ display: 'flex', gap: 10 }}>
              <BtnSecondary onClick={fetchData}><Icon name="refresh" size={15} />{t('screens.aiGovernance.refresh')}</BtnSecondary>
              <BtnPrimary onClick={exportCSV}><Icon name="download" size={15} />{t('screens.aiGovernance.exportCsv')}</BtnPrimary>
            </div>
          }
        />

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
          <StatCard label={t('screens.aiGovernance.totalInteractions')} value={(stats.totalInteractions || 0).toLocaleString()} sub={t('screens.aiGovernance.allTime')} color={S.burgundy} />
          <StatCard label={t('screens.aiGovernance.uniqueUsers')} value={stats.uniqueUsers || 0} sub={t('screens.aiGovernance.acrossClaims', { count: stats.uniqueClaims || 0 })} color="#3b82f6" />
          <StatCard label={t('screens.aiGovernance.estimatedCost')} value={`$${stats.estimatedCostUSD || '0.00'}`} sub={t('screens.aiGovernance.tokenSummary', { input: fmtTokens(stats.totalInputTokens || 0), output: fmtTokens(stats.totalOutputTokens || 0) })} color="#10b981" />
        </div>

        {/* Model usage card */}
        <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 14, padding: '20px 24px', marginBottom: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary, marginBottom: 12 }}>{t('screens.aiGovernance.modelUsage')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ flex: 1, background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary }}>claude-sonnet-4-6</div>
                <div style={{ fontSize: 11, color: S.textMuted, marginTop: 2 }}>{t('screens.aiGovernance.primaryModel')}</div>
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: S.burgundy }}>{stats.totalInteractions || 0}</div>
            </div>
          </div>
        </div>

        {/* Bias & Fairness placeholder */}
        <div style={{ background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', border: `1px solid ${S.borderLight}`, borderRadius: 14, padding: '20px 24px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
          <Icon name="check-circle" size={24} color="#94a3b8" />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: S.textPrimary }}>{t('screens.aiGovernance.biasTitle')}</div>
            <div style={{ fontSize: 13, color: S.textMuted, marginTop: 2 }}>{t('screens.aiGovernance.biasDesc')}</div>
          </div>
        </div>

        {/* Interaction log table */}
        <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 14, overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{ padding: '16px 24px', borderBottom: `1px solid ${S.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{t('screens.aiGovernance.interactionLog')}</div>
            <input
              placeholder={t('screens.aiGovernance.filterByClaim')}
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              style={{ padding: '8px 14px', border: `1px solid ${S.borderLight}`, borderRadius: 10, fontSize: 13, color: S.textPrimary, background: S.bgCard, outline: 'none', width: 220 }}
            />
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: S.bgCard }}>
                  <TH>{t('screens.aiGovernance.colTimestamp')}</TH>
                  <TH>{t('screens.aiGovernance.colUser')}</TH>
                  <TH>{t('screens.aiGovernance.colClaim')}</TH>
                  <TH>{t('screens.aiGovernance.colQuestion')}</TH>
                  <TH>{t('screens.aiGovernance.colModel')}</TH>
                  <TH>{t('screens.aiGovernance.colInTokens')}</TH>
                  <TH>{t('screens.aiGovernance.colOutTokens')}</TH>
                  <TH>{t('screens.aiGovernance.colContextHash')}</TH>
                  <TH>{t('screens.aiGovernance.colResponseHash')}</TH>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: S.textMuted, fontSize: 14 }}>{t('screens.aiGovernance.loading')}</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan={9}><EmptyState icon="cpu" title={t('screens.aiGovernance.noInteractionsTitle')} subtitle={t('screens.aiGovernance.noInteractionsDesc')} /></td></tr>
                ) : logs.map((log, i) => (
                  <tr
                    key={log._id || i}
                    onClick={() => setSelectedLog(log)}
                    style={{ borderBottom: `1px solid ${S.borderLight}`, background: i % 2 === 0 ? '#fff' : S.bgCard, cursor: 'pointer', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : S.bgCard}
                  >
                    <TD style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtTime(log.timestamp)}</TD>
                    <TD style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{log.user?.name || '—'}</TD>
                    <TD><span style={{ background: 'rgba(128,0,32,0.07)', color: S.burgundy, padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{log.content?.claimNumber || '—'}</span></TD>
                    <TD style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: S.textSecondary }}>{log.content?.questionSummary || '—'}</TD>
                    <TD style={{ fontSize: 12, color: S.textMuted }}>{log.content?.modelUsed || '—'}</TD>
                    <TD style={{ textAlign: 'right', fontSize: 12 }}>{(log.content?.inputTokens || 0).toLocaleString()}</TD>
                    <TD style={{ textAlign: 'right', fontSize: 12 }}>{(log.content?.outputTokens || 0).toLocaleString()}</TD>
                    <TD style={{ fontFamily: 'monospace', fontSize: 11, color: S.textMuted }}>{hashShort(log.content?.contextHash)}</TD>
                    <TD style={{ fontFamily: 'monospace', fontSize: 11, color: S.textMuted }}>{hashShort(log.content?.responseHash)}</TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${S.borderLight}`, display: 'flex', gap: 8, justifyContent: 'center' }}>
              <BtnSecondary onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>{t('screens.aiGovernance.prev')}</BtnSecondary>
              <span style={{ padding: '8px 16px', fontSize: 13, color: S.textMuted }}>{t('screens.aiGovernance.pageOf', { page, total: totalPages })}</span>
              <BtnSecondary onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>{t('screens.aiGovernance.next')}</BtnSecondary>
            </div>
          )}
        </div>
      </div>

      {selectedLog && <DetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />}
    </AppLayout>
  );
};

export default AIGovernanceScreen;
